/* eslint-disable react/jsx-key */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
// import DownloadIcon from '@mui/icons-material/Download';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DomainIcon from '@mui/icons-material/Domain';
import GetAppIcon from '@mui/icons-material/GetApp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WorkIcon from '@mui/icons-material/Work';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Document,
  Page,
  pdf,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatPhoneNumber } from 'utils/Helpers';

import { ShortlistPopup } from './ShortlistPopup';

interface CandidateHeaderProps {
  candidateName: string;
  availability: string;
  availabilityDate?: string;
  email: string;
  phone: string;
  location: string;
  matchScore?: number;
  visaStatus?: string;
  payRate?: string;
  source?: string;
  jobTitle?: string;
  showDownload?: boolean;
  showShortlist?: boolean;
  onDownload?: () => void;
  onShortlist?: () => void;
  onShortlistForJob?: () => void;
  onBack?: () => void;
  candidateInternalId?: string;
  getOverviewId?: {
    withGap: string;
    withoutGap: string;
  };
  jobMatchCandidateId?: string;
  candidateId?: string;
  candidateDetails?: any;
  Params?: any;
  sourceLink?: string;
  percentageDetail?: any;
  goal?: string;
  expFrom?: number | null;
  expTo?: number | null;
}

export const CandidateHeader: React.FC<CandidateHeaderProps> = ({
  candidateName,
  availability,
  availabilityDate,
  email,
  phone,
  location,
  matchScore,
  visaStatus,
  payRate,
  source,
  jobTitle,
  showDownload = false,
  showShortlist,
  onDownload,
  onShortlist,
  onShortlistForJob,
  onBack,
  candidateInternalId,
  getOverviewId,
  jobMatchCandidateId,
  candidateId,
  candidateDetails,
  Params,
  sourceLink,
  percentageDetail,
  goal,
  expFrom,
  expTo,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [hasShortlisted, setHasShortlisted] = useState(!!showShortlist);
  const [openPercentageModal, setOpenPercentageModal] = useState(false);
  const [goalSectionHeight, setGoalSectionHeight] = useState<number | 'auto'>(
    'auto',
  );
  const [isDragging, setIsDragging] = useState(false);
  const [formulaStepsExpanded, setFormulaStepsExpanded] = useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (openPercentageModal && contentRef.current) {
      const height = contentRef.current.scrollHeight;
      const restrictedHeight = Math.max(100, Math.min(600, height));
      setGoalSectionHeight(restrictedHeight);
    }
  }, [goal, openPercentageModal]);

  // Resize handlers for the goal section
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const modalElement = document.querySelector('[data-goal-container]');
      if (!modalElement) return;

      const rect = modalElement.getBoundingClientRect();
      const newHeight = Math.max(100, Math.min(600, e.clientY - rect.top));
      setGoalSectionHeight(newHeight);
    },
    [isDragging],
  );

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Use percentageDetail prop or fallback to candidateDetails?.mathematicalBreakdown
  const percentageData =
    percentageDetail || candidateDetails?.mathematicalBreakdown;

  useEffect(() => {
    setHasShortlisted(!!showShortlist);
  }, [showShortlist]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // Detect basePath from current URL (e.g., /sales or /management)
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const basePath =
        pathSegments.length > 0 ? `/${pathSegments[0]}` : '/sales';

      navigate(`${basePath}/job-post/view-job/iprise-job/${params?.jobId}`, {
        state: { Params: Params, candidateDetails: candidateDetails },
      });
    }
  };

  const handleShortlistClick = () => {
    setPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setPopoverOpen(false);
  };

  // PDF Styles
  const pdfStyles = StyleSheet.create({
    breakdownBox: {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      marginBottom: 15,
      padding: 15,
    },
    breakdownItem: {
      alignItems: 'center',
      flexDirection: 'row',
      marginBottom: 10,
    },
    breakdownLabel: {
      color: '#4b5563',
      fontSize: 12,
      width: 120,
    },
    breakdownValue: {
      color: '#374151',
      fontSize: 12,
      fontWeight: 'bold',
      textAlign: 'right',
      width: 60,
    },
    candidateName: {
      color: '#1f2937',
      fontSize: 24,
      fontWeight: 'bold',
      marginBottom: 10,
    },
    considerationTag: {
      backgroundColor: '#fee2e2',
      border: '1px solid #fecaca',
      borderRadius: 4,
      color: '#991b1b',
      fontSize: 11,
      marginBottom: 5,
      padding: '4px 12px',
    },
    header: {
      borderBottom: '2px solid #6366f1',
      marginBottom: 20,
      paddingBottom: 10,
    },
    infoItem: {
      alignItems: 'center',
      color: '#6b7280',
      flexDirection: 'row',
      fontSize: 13,
      gap: 5,
    },
    infoRow: {
      flexDirection: 'row',
      gap: 15,
      marginBottom: 10,
    },
    matchScore: {
      textAlign: 'right',
    },
    matchScoreLabel: {
      color: '#9ca3af',
      fontSize: 11,
      fontWeight: 'bold',
      marginBottom: 5,
      textTransform: 'uppercase',
    },
    matchScoreValue: {
      color: '#10b981',
      fontSize: 38,
      fontWeight: 'bold',
    },
    nameRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 15,
    },
    noBreak: {
      break: false,
    },
    page: {
      fontFamily: 'Helvetica',
      fontSize: 12,
      padding: 30,
    },
    pageBreak: {
      break: true,
    },
    progressBar: {
      backgroundColor: '#e5e7eb',
      borderRadius: 4,
      flex: 1,
      height: 8,
      marginHorizontal: 10,
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
    },
    rankBadge: {
      alignSelf: 'flex-start',
      backgroundColor: '#4f46e5',
      color: '#ffffff',
      fontSize: 14,
      fontWeight: 'bold',
      marginBottom: 10,
      padding: '4px 16px',
      width: 'auto',
    },
    section: {
      borderTop: '1px solid #e5e7eb',
      marginBottom: 20,
      paddingTop: 15,
    },
    sectionTitle: {
      color: '#166534',
      fontSize: 11,
      fontWeight: 'bold',
      marginBottom: 10,
      textTransform: 'uppercase',
    },
    skillTag: {
      backgroundColor: '#dbeafe',
      borderRadius: 20,
      color: '#1e40af',
      fontSize: 12,
      marginBottom: 5,
      padding: '4px 12px',
    },
    summaryBox: {
      backgroundColor: '#f9fafb',
      borderRadius: 8,
      marginBottom: 15,
      padding: 15,
    },
    summaryText: {
      color: '#374151',
      fontSize: 14,
      fontStyle: 'italic',
      lineHeight: 1.6,
    },
    tag: {
      backgroundColor: '#dcfce7',
      border: '1px solid #bbf7d0',
      borderRadius: 4,
      color: '#166534',
      fontSize: 11,
      marginBottom: 5,
      padding: '4px 12px',
    },
    tagContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
  });

  const identityPercentage = (
    candidateDetails?.mathematicalBreakdown ?? []
  ).find((row) => row?.component === 'Identity Match');

  let matchedSkills = [];
  let secondarySkills = [];

  let primarySkillAnalysis: any = null;
  let secondarySkillAnalysis: any = null;

  (candidateDetails?.mathematicalBreakdown ?? []).map(
    (row: any, index: number) => {
      if (row?.component === 'Primary Skills') {
        matchedSkills = matchedSkills.concat(row?.matchedSkills ?? []);
        primarySkillAnalysis = row?.skillAnalysis;
      }
      if (row?.component === 'Secondary Skills') {
        secondarySkills = secondarySkills.concat(row?.matchedSkills ?? []);
        secondarySkillAnalysis = row?.skillAnalysis;
      }
    },
  );

  // PDF Document Component
  const CandidatePDFDocument = () => {
    const scoreBreakdown =
      Array.isArray(percentageData) && percentageData?.length > 0
        ? percentageData.map((row: any, index: number) => {
            const score = parseFloat(row?.score) || 0;
            const max = parseFloat(row?.maxPercentage) || 0;
            const percentage = Math.min(max > 0 ? (score / max) * 100 : 0, 100);
            const colors = [
              '#a855f7',
              '#06b6d4',
              '#f59e0b',
              '#10b981',
              '#6366f1',
            ];
            const formattedScore =
              score % 1 === 0 ? score.toString() : score.toFixed(1);
            const formattedMax = Math.round(max).toString();
            return {
              color: colors[index % colors.length],
              identityClass: row?.identityClass,
              label: row?.component || 'Component',
              percentage,
              value: `${formattedScore}/${formattedMax}`,
            };
          })
        : [];

    // Get location string
    const locationStr =
      candidateDetails?.candidate?.city || candidateDetails?.candidate?.state
        ? `${String(candidateDetails?.candidate?.city || '')}${
            candidateDetails?.candidate?.city &&
            candidateDetails?.candidate?.state
              ? ', '
              : ''
          }${String(candidateDetails?.candidate?.state || '')}`
        : location || '-';

    // Get experience string
    const experienceStr = candidateDetails?.candidate?.overallExperience
      ? `${String(candidateDetails?.candidate?.overallExperience)} Years`
      : '-';

    // Get work status string
    const workStatusStr =
      candidateDetails?.candidate?.workStatus?.name || visaStatus || '-';

    return (
      <Document>
        <Page size="A4" style={pdfStyles.page} wrap>
          {/* Rank Badge */}
          {candidateDetails?.rank && (
            <View style={pdfStyles.rankBadge}>
              <Text>#{candidateDetails.rank}</Text>
            </View>
          )}

          {/* Header: Name and Match Score */}
          <View style={pdfStyles.nameRow} wrap={false}>
            <View>
              <Text style={pdfStyles.candidateName}>{candidateName || ''}</Text>
              {(locationStr !== '-' ||
                experienceStr !== '-' ||
                workStatusStr !== '-') && (
                <View style={pdfStyles.infoRow}>
                  {locationStr !== '-' && (
                    <View style={pdfStyles.infoItem}>
                      <Text
                        style={{
                          color: '#ef4444',
                          fontSize: 16,
                          fontWeight: 'bold',
                          marginRight: 6,
                        }}>
                        •
                      </Text>
                      <Text style={{ color: '#6b7280', fontSize: 13 }}>
                        {locationStr}
                      </Text>
                    </View>
                  )}
                  {experienceStr !== '-' && (
                    <View style={pdfStyles.infoItem}>
                      <Text
                        style={{
                          color: '#92400e',
                          fontSize: 16,
                          fontWeight: 'bold',
                          marginRight: 6,
                        }}>
                        •
                      </Text>
                      <Text style={{ color: '#6b7280', fontSize: 13 }}>
                        {experienceStr}
                      </Text>
                    </View>
                  )}
                  {workStatusStr !== '-' && (
                    <View style={pdfStyles.infoItem}>
                      <Text
                        style={{
                          color: '#ef4444',
                          fontSize: 16,
                          fontWeight: 'bold',
                          marginRight: 6,
                        }}>
                        •
                      </Text>
                      <Text style={{ color: '#6b7280', fontSize: 13 }}>
                        {workStatusStr}
                      </Text>
                    </View>
                  )}
                </View>
              )}
            </View>
            <View style={pdfStyles.matchScore}>
              <Text style={pdfStyles.matchScoreLabel}>Match Score</Text>
              <Text style={pdfStyles.matchScoreValue}>{matchScore || 0}%</Text>
            </View>
          </View>

          {/* Score Breakdown */}
          {scoreBreakdown.length > 0 && (
            <View style={pdfStyles.breakdownBox} wrap={false}>
              <Text style={pdfStyles.sectionTitle}>SCORE BREAKDOWN (V2.1)</Text>
              {scoreBreakdown.map((item: any, index: number) => {
                if (item?.component === 'Identity Match') {
                  return null;
                }
                return (
                  <View key={index} style={pdfStyles.breakdownItem}>
                    <Text style={pdfStyles.breakdownLabel}>{item.label}</Text>
                    <View style={pdfStyles.progressBar}>
                      <View
                        style={[
                          pdfStyles.progressFill,
                          {
                            backgroundColor: item.color,
                            width: `${String(item.percentage)}%`,
                          },
                        ]}
                      />
                    </View>
                    <Text style={pdfStyles.breakdownValue}>{item.value}</Text>
                  </View>
                );
              })}
              {identityPercentage ? (
                <View
                  style={{
                    alignItems: 'center',
                    backgroundColor: (() => {
                      const status =
                        identityPercentage?.identityStatus?.toLowerCase();
                      if (status?.includes('exact match')) return '#d1fae5';
                      if (status?.includes('related role')) return '#fef3c7';
                      if (status?.includes('role mismatch')) return '#fee2e2';
                      return '#d1fae5'; // default to match
                    })(),
                    borderColor: (() => {
                      const status =
                        identityPercentage?.identityStatus?.toLowerCase();
                      if (status?.includes('exact match')) return '#6ee7b7';
                      if (status?.includes('related role')) return '#fcd34d';
                      if (status?.includes('role mismatch')) return '#f87171';
                      return '#6ee7b7'; // default to match
                    })(),
                    borderRadius: 10,
                    borderWidth: 1,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: 10,
                    paddingBottom: 10,
                    paddingLeft: 20,
                    paddingRight: 20,
                    paddingTop: 10,
                  }}>
                  <Text
                    style={{
                      color: '#6b7280',
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}>
                    {identityPercentage?.component}
                  </Text>
                  <Text
                    style={{
                      color: (() => {
                        const status =
                          identityPercentage?.identityStatus?.toLowerCase();
                        if (status?.includes('exact match')) return '#047857';
                        if (status?.includes('related role')) return '#b45309';
                        if (status?.includes('role mismatch')) return '#dc2626';
                        return '#047857'; // default to match
                      })(),
                      fontSize: 12,
                      fontWeight: 'bold',
                      textAlign: 'right',
                    }}>
                    {identityPercentage?.identityStatus} (
                    {identityPercentage?.identityPercentage}%)
                  </Text>
                </View>
              ) : null}
            </View>
          )}

          {/* Summary */}
          {candidateDetails?.justification && (
            <View style={pdfStyles.summaryBox} wrap={false}>
              <Text style={pdfStyles.summaryText}>
                {String(candidateDetails.justification)}
              </Text>
            </View>
          )}

          {/* Strengths */}
          {Array.isArray(candidateDetails?.strengths) &&
            candidateDetails?.strengths?.length > 0 && (
              <View style={pdfStyles.section} wrap={false}>
                <Text style={pdfStyles.sectionTitle}>✓ Strengths</Text>
                <View style={pdfStyles.tagContainer}>
                  {candidateDetails.strengths.map(
                    (strength: string, i: number) => (
                      <View key={i} style={pdfStyles.tag}>
                        <Text>✓ {strength}</Text>
                      </View>
                    ),
                  )}
                </View>
              </View>
            )}
          {/* Considerations */}
          {Array.isArray(candidateDetails?.gapsConcerns) &&
            candidateDetails?.gapsConcerns?.length > 0 && (
              <View style={pdfStyles.section} wrap={false}>
                <Text style={pdfStyles.sectionTitle}>⚠ Considerations</Text>
                <View style={pdfStyles.tagContainer}>
                  {candidateDetails.gapsConcerns.map(
                    (consideration: string, i: number) => (
                      <View key={i} style={pdfStyles.considerationTag}>
                        <Text>⚠ {consideration}</Text>
                      </View>
                    ),
                  )}
                </View>
              </View>
            )}
          {/* Matched Skills */}
          {Array.isArray(matchedSkills) && matchedSkills?.length > 0 && (
            <View style={pdfStyles.section} wrap={false}>
              <Text style={pdfStyles.sectionTitle}>
                ✓ Matched Skills ({matchedSkills?.length})
              </Text>
              <View style={pdfStyles.tagContainer}>
                {matchedSkills?.map((s: any, i: number) => {
                  // Determine the symbol based on match type
                  const symbol =
                    s?.matchType === 'direct_primary'
                      ? '✓✓'
                      : s?.matchType === 'direct_secondary'
                      ? '✓'
                      : s?.matchType === 'ecosystem_primary'
                      ? '≈'
                      : s?.matchType === 'ecosystem_secondary'
                      ? '~'
                      : '✗✗';

                  // Determine the text to display
                  const displayText = [
                    'ecosystem_primary',
                    'ecosystem_secondary',
                  ].includes(s?.matchType)
                    ? `${symbol} ${String(s?.skill)} → ${String(
                        s?.matchedWith,
                      )}`
                    : `${symbol} ${String(s?.matchedWith)}`;

                  return (
                    <View key={i} style={pdfStyles.tag}>
                      <Text>{displayText}</Text>
                    </View>
                  );
                })}
              </View>
            </View>
          )}

          {/* Top Skills */}
          {/* {topSkillsList.length > 0 && (
            <View style={pdfStyles.section} wrap={false}>
              <Text style={pdfStyles.sectionTitle}>Top Skills</Text>
              <View style={pdfStyles.tagContainer}>
                {topSkillsList.map((skill: string, i: number) => (
                  <View key={i} style={pdfStyles.skillTag}>
                    <Text>{skill}</Text>
                  </View>
                ))}
              </View>
            </View>
          )} */}
        </Page>
      </Document>
    );
  };

  // Download PDF function
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDownloadPDF = async () => {
    try {
      const doc = <CandidatePDFDocument />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      saveAs(blob, `${candidateName || 'Candidate'}_Match_Details.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#FAFAFB',
        borderBottom: '1px solid #ECECEC',
        minWidth: 0,
        padding: '16px 20px 12px 20px',
      }}>
      {/* Candidate Details Row */}
      <div
        style={{
          alignItems: 'flex-start',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          justifyContent: 'space-between',
          minWidth: 0,
          width: '100%',
        }}>
        <div
          style={{
            alignItems: 'flex-start',
            display: 'flex',
            flex: 1,
            gap: '16px',
            minWidth: 0,
          }}>
          {/* Back Button */}
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexShrink: 0,
            }}>
            <IconButton
              onClick={handleBack}
              sx={{
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
                border: '1px solid #dddddd',
                borderRadius: '6px',
                height: '32px',
                marginRight: '16px',
                padding: '4px',
                width: '32px',
              }}>
              <ArrowBackIcon sx={{ color: '#3f3f3f', fontSize: '16px' }} />
            </IconButton>
          </div>
          {/* Candidate Name and Info */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              minWidth: 0,
              width: '100%',
            }}>
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '24px',
                minWidth: 0,
              }}>
              {/* Candidate Name */}
              <Typography
                sx={{
                  color: '#242424',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '20px',
                }}>
                {candidateName ? candidateName : '-'}
              </Typography>

              {/* Availability */}
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                }}>
                {availability && (
                  <>
                    <CheckCircleIcon
                      sx={{
                        color: '#0E700E',
                        fontSize: '10px',
                        marginRight: '4px',
                      }}
                    />
                    <Typography
                      sx={{
                        color: '#0E700E',
                        fontFamily: 'Inter',
                        fontSize: '10px',
                        fontWeight: 600,
                        lineHeight: '14px',
                        marginRight: '4px',
                      }}>
                      {availability}
                    </Typography>
                  </>
                )}
                {availabilityDate && (
                  <>
                    <span
                      style={{
                        backgroundColor: '#0E700E',
                        borderRadius: '100%',
                        height: '3px',
                        marginRight: '4px',
                        width: '3px',
                      }}
                    />
                    <Typography
                      sx={{
                        color: '#0E700E',
                        fontFamily: 'Inter',
                        fontSize: '10px',
                        fontWeight: 600,
                        lineHeight: '14px',
                        marginRight: '4px',
                      }}>
                      {availabilityDate}
                    </Typography>
                  </>
                )}
              </div>
            </div>

            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                minWidth: 0,
                width: '100%',
              }}>
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '16px',
                  minWidth: 0,
                }}>
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '14px',
                  }}>
                  {email ? email : '-'}
                </Typography>
                <span
                  style={{
                    backgroundColor: '#D9D9D9',
                    borderRadius: '100%',
                    height: '6px',
                    width: '6px',
                  }}
                />
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '14px',
                  }}>
                  {phone ? formatPhoneNumber(phone) : '-'}
                </Typography>
                <span
                  style={{
                    backgroundColor: '#D9D9D9',
                    borderRadius: '100%',
                    height: '6px',
                    width: '6px',
                  }}
                />
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '14px',
                  }}>
                  {location ? location : '-'}
                </Typography>
              </div>
            </div>
            {/* Badges Row */}
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                minWidth: 0,
                width: '100%',
              }}>
              {/* Match Score Badge */}
              {matchScore !== undefined && (
                <Chip
                  label={`${matchScore}%`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenPercentageModal(true);
                  }}
                  sx={{
                    '& .MuiChip-label': {
                      lineHeight: '14px',
                      padding: '0 2px',
                    },
                    backgroundColor: '#EBF0FF',
                    border: '1px solid #7995E8',
                    color: '#2453C3',
                    cursor: 'pointer',
                    fontFamily: 'Inter',
                    fontSize: '10px',
                    fontWeight: 600,
                    height: '20px',
                    padding: '2px 4px',
                  }}
                />
              )}
              {/* Visa Status Badge */}
              {visaStatus && (
                <Chip
                  label={visaStatus}
                  sx={{
                    '& .MuiChip-label': {
                      lineHeight: '14px',
                      padding: '0 2px',
                    },
                    backgroundColor: '#F0F0F0',
                    border: '1px solid #DBDBDB',
                    color: '#7C7C7C',
                    fontFamily: 'Inter',
                    fontSize: '10px',
                    fontWeight: 600,
                    height: '20px',
                    padding: '2px 4px',
                  }}
                />
              )}
              {/* Bill Rate Badge */}
              {payRate && (
                <Chip
                  label={`Pay Rate • ${payRate}`}
                  sx={{
                    '& .MuiChip-label': {
                      lineHeight: '14px',
                      padding: '0 2px',
                    },
                    backgroundColor: '#FAF5F1',
                    border: '1px solid #D8BB9F',
                    color: '#C36824',
                    fontFamily: 'Inter',
                    fontSize: '10px',
                    fontWeight: 600,
                    height: '20px',
                    padding: '2px 4px',
                  }}
                />
              )}
              {source &&
              ['email', 'manual'].includes(String(source).toLowerCase()) ? (
                <Chip
                  label={source?.toUpperCase()}
                  sx={{
                    '& .MuiChip-label': {
                      lineHeight: '14px',
                      padding: '0 2px',
                    },
                    backgroundColor: '#FFE3E3',
                    border: '1px solid #E87979',
                    color: '#C32424',
                    fontFamily: 'Inter',
                    fontSize: '10px',
                    fontWeight: 600,
                    height: '20px',
                    padding: '2px 4px',
                    textTransform: 'capitalize',
                  }}
                />
              ) : sourceLink ? (
                <Tooltip title={sourceLink} arrow>
                  <Chip
                    component="a"
                    href={sourceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    aria-label={`Open source link ${sourceLink}`}
                    label={source?.toUpperCase()}
                    sx={{
                      '& .MuiChip-label': {
                        lineHeight: '14px',
                        maxWidth: 200,
                        overflow: 'hidden',
                        padding: '0 2px',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                      backgroundColor: '#FFE3E3',
                      border: '1px solid #E87979',
                      color: '#C32424',
                      fontFamily: 'Inter',
                      fontSize: '10px',
                      fontWeight: 600,
                      height: '20px',
                      padding: '2px 4px',
                      textTransform: 'none',
                    }}
                  />
                </Tooltip>
              ) : source ? (
                <Chip
                  label={source?.toUpperCase()}
                  sx={{
                    '& .MuiChip-label': {
                      lineHeight: '14px',
                      padding: '0 2px',
                    },
                    backgroundColor: '#FFE3E3',
                    border: '1px solid #E87979',
                    color: '#C32424',
                    fontFamily: 'Inter',
                    fontSize: '10px',
                    fontWeight: 600,
                    height: '20px',
                    padding: '2px 4px',
                    textTransform: 'capitalize',
                  }}
                />
              ) : (
                ''
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0,
            gap: '12px',
            marginLeft: 'auto',
          }}>
          {showDownload && (
            <IconButton
              onClick={onDownload}
              sx={{
                '&:hover': {
                  backgroundColor: '#f9fafb',
                },
                backgroundColor: '#ffffff',
                border: '1px solid #dddddd',
                borderRadius: '6px',
                padding: '6px',
              }}>
              <GetAppIcon sx={{ color: '#616161', fontSize: '16px' }} />
            </IconButton>
          )}
          {!hasShortlisted && (
            <Button
              onClick={handleShortlistClick}
              sx={{
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
                backgroundColor: '#FFFFFF',
                border: '1px solid #dddddd',
                borderRadius: '6px',
                color: '#464646',
                fontFamily: 'Inter',
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '0.24px',
                padding: '6px 12px',
                textTransform: 'none',
              }}>
              Shortlist Candidate
            </Button>
          )}
        </div>
      </div>

      {/* Shortlist Popup */}
      <ShortlistPopup
        candidateName={candidateName}
        jobTitle={jobTitle}
        onClose={handlePopoverClose}
        onProceed={(selectedItems) => {
          setHasShortlisted(true);
          if (onShortlistForJob) {
            onShortlistForJob();
          }
        }}
        open={popoverOpen}
        candidateInternalId={candidateInternalId}
        getOverviewId={getOverviewId}
        jobMatchCandidateId={jobMatchCandidateId}
        candidateId={candidateId}
        candidateDetails={candidateDetails}
      />

      {/* Score View Modal */}
      <Modal
        open={openPercentageModal}
        onClose={() => setOpenPercentageModal(false)}
        disableAutoFocus
        disableEnforceFocus>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '100vh',
            justifyContent: 'center',
            outline: 'none',
          }}>
          <Box
            sx={{
              bgcolor: '#ffffff',
              border: '1px solid #6366f1',
              borderRadius: '12px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '95vh',
              overflow: 'hidden',
              position: 'relative',
              width: '80%',
            }}>
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: '#ffffff',
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '8px 16px',
              }}>
              {/* Download PDF button commented out */}
              {/* <Button
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                size="small"
                sx={{
                  color: '#6366f1',
                  fontSize: '12px',
                  marginLeft: '40px',
                  textTransform: 'none',
                }}
                variant="outlined">
                Download PDF
              </Button> */}
              <IconButton
                size="small"
                onClick={() => setOpenPercentageModal(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {/* Rank Badge - Top Left */}
            {candidateDetails?.rank && (
              <Box
                sx={{
                  backgroundColor: '#4f46e5',
                  borderBottomRightRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 800,
                  left: 0,
                  padding: '4px 16px',
                  position: 'absolute',
                  top: 0,
                  zIndex: 10,
                }}>
                #{candidateDetails?.rank}
              </Box>
            )}

            {/* Fixed Goal Section */}
            <Box
              data-goal-container
              sx={{
                borderBottom: '1px solid #f3f4f6',
                flexShrink: 0,
                p: 2,
              }}>
              {goal &&
                (() => {
                  try {
                    const goalData =
                      typeof goal === 'string' ? JSON.parse(goal) : goal;
                    const introduction = goalData?.introduction;
                    const requiredIdentity = goalData?.requiredIdentity;
                    const mustHaveSkills = goalData?.skills?.mustHave || [];
                    const primarySkills = goalData?.skills?.primary || [];
                    const secondarySkills = goalData?.skills?.secondary || [];

                    const experienceText =
                      expFrom !== null && expFrom !== undefined
                        ? `${expFrom}${
                            expTo && expTo !== expFrom && expTo !== 0
                              ? `-${expTo}`
                              : '+'
                          } years`
                        : null;

                    return (
                      <>
                        <Box
                          ref={contentRef}
                          sx={{
                            borderRadius: '8px',
                            height:
                              goalSectionHeight === 'auto'
                                ? 'auto'
                                : `${goalSectionHeight}px`,
                            mb: 1.5,
                            overflowY: 'auto',
                            p: 1.5,
                          }}>
                          {/* Job Title Header */}
                          {jobTitle && (
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                sx={{
                                  color: '#111827',
                                  fontSize: '18px',
                                  fontWeight: 700,
                                  mb: 0.5,
                                }}>
                                {jobTitle}
                              </Typography>
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1.5,
                                }}>
                                {requiredIdentity && (
                                  <Box
                                    sx={{
                                      backgroundColor: '#ede9fe',
                                      borderRadius: '4px',
                                      color: '#6b21a8',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      px: 1.5,
                                      py: 0.5,
                                    }}>
                                    {requiredIdentity}
                                  </Box>
                                )}
                                {experienceText && (
                                  <Box
                                    sx={{
                                      backgroundColor: '#f3f4f6',
                                      borderRadius: '4px',
                                      color: '#374151',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      px: 1.5,
                                      py: 0.5,
                                    }}>
                                    {experienceText}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          )}

                          {introduction && (
                            <Typography
                              sx={{
                                color: '#374151',
                                fontSize: '13px',
                                fontWeight: 400,
                                lineHeight: 1.5,
                                mb: 1,
                              }}>
                              {introduction}
                            </Typography>
                          )}

                          {(mustHaveSkills.length > 0 ||
                            primarySkills.length > 0) && (
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                sx={{
                                  alignItems: 'center',
                                  color: '#4f46e5',
                                  display: 'flex',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  gap: 0.5,
                                  mb: 1,
                                }}>
                                ● Primary Skills (
                                {mustHaveSkills.length + primarySkills.length})
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.75,
                                }}>
                                {mustHaveSkills.map(
                                  (skill: string, i: number) => (
                                    <Box
                                      key={`must-${i}`}
                                      sx={{
                                        backgroundColor: '#e0e7ff',
                                        borderRadius: '4px',
                                        color: '#4338ca',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        px: 1.5,
                                        py: 0.5,
                                      }}>
                                      {skill}
                                    </Box>
                                  ),
                                )}
                                {primarySkills.map(
                                  (skill: string, i: number) => (
                                    <Box
                                      key={`primary-${i}`}
                                      sx={{
                                        backgroundColor: '#e0e7ff',
                                        borderRadius: '4px',
                                        color: '#4338ca',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        px: 1.5,
                                        py: 0.5,
                                      }}>
                                      {skill}
                                    </Box>
                                  ),
                                )}
                              </Box>
                            </Box>
                          )}

                          {secondarySkills.length > 0 && (
                            <Box>
                              <Typography
                                sx={{
                                  alignItems: 'center',
                                  color: '#6b7280',
                                  display: 'flex',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  gap: 0.5,
                                  mb: 1,
                                }}>
                                ○ Secondary Skills ({secondarySkills.length})
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.75,
                                }}>
                                {secondarySkills.map(
                                  (skill: string, i: number) => (
                                    <Box
                                      key={i}
                                      sx={{
                                        backgroundColor: '#f3f4f6',
                                        borderRadius: '4px',
                                        color: '#4b5563',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        px: 1.5,
                                        py: 0.5,
                                      }}>
                                      {skill}
                                    </Box>
                                  ),
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                        <Box
                          onMouseDown={handleMouseDown}
                          sx={{
                            '&:active': {
                              backgroundColor: '#3b82f6',
                            },
                            '&:hover': {
                              backgroundColor: '#60a5fa',
                            },
                            alignItems: 'center',
                            backgroundColor: isDragging ? '#3b82f6' : '#e5e7eb',
                            cursor: 'ns-resize',
                            display: 'flex',
                            height: '5px',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s ease',
                            width: '100%',
                          }}>
                          <img
                            src={require('assets/images/split-v.svg').default}
                            alt="Resize"
                            style={{
                              height: 'auto',
                              rotate: '90deg',
                              width: '24px',
                            }}
                          />
                        </Box>
                      </>
                    );
                  } catch (e) {
                    return (
                      <>
                        <Box
                          ref={contentRef}
                          sx={{
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #86efac',
                            borderRadius: '8px',
                            height:
                              goalSectionHeight === 'auto'
                                ? 'auto'
                                : `${goalSectionHeight}px`,
                            mb: 1.5,
                            overflowY: 'auto',
                            p: 1.5,
                          }}>
                          <Typography
                            sx={{
                              color: '#166534',
                              fontSize: '11px',
                              fontWeight: 700,
                              letterSpacing: '0.5px',
                              mb: 1,
                              textTransform: 'uppercase',
                            }}>
                            Goal
                          </Typography>
                          <Typography
                            sx={{
                              color: '#374151',
                              fontSize: '13px',
                              fontWeight: 400,
                              lineHeight: 1.6,
                            }}>
                            {typeof goal === 'string'
                              ? goal
                              : JSON.stringify(goal)}
                          </Typography>
                        </Box>
                        <Box
                          onMouseDown={handleMouseDown}
                          sx={{
                            alignItems: 'center',
                            cursor: 'ns-resize',
                            display: 'flex',
                            height: 'auto',
                            justifyContent: 'center',
                            padding: '4px 0',
                            width: '100%',
                          }}>
                          <img
                            src={require('assets/images/split-v.svg').default}
                            alt="Resize"
                            style={{
                              height: 'auto',
                              width: '24px',
                            }}
                          />
                        </Box>
                      </>
                    );
                  }
                })()}
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
              {/* Header: Name and Match Score */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 3,
                  mt: 1,
                }}>
                <Box>
                  <Typography
                    sx={{
                      color: '#1f2937',
                      fontSize: '24px',
                      fontWeight: 700,
                    }}>
                    {candidateName || ''}
                  </Typography>
                  {(candidateDetails?.candidate?.city ||
                    candidateDetails?.candidate?.state ||
                    candidateDetails?.candidate?.overallExperience ||
                    candidateDetails?.candidate?.workStatus?.name) && (
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 2,
                        mt: 1,
                      }}>
                      {(candidateDetails?.candidate?.city ||
                        candidateDetails?.candidate?.state) && (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 0.5,
                          }}>
                          <LocationOnIcon
                            sx={{ color: '#ef4444', fontSize: '16px' }}
                          />
                          <Typography
                            sx={{ color: '#6b7280', fontSize: '13px' }}>
                            {candidateDetails?.candidate?.city || ''}
                            {candidateDetails?.candidate?.city &&
                            candidateDetails?.candidate?.state
                              ? ', '
                              : ''}
                            {candidateDetails?.candidate?.state}
                          </Typography>
                        </Box>
                      )}
                      {candidateDetails?.candidate?.overallExperience && (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 0.5,
                          }}>
                          <WorkIcon
                            sx={{ color: '#92400e', fontSize: '16px' }}
                          />
                          <Typography
                            sx={{ color: '#6b7280', fontSize: '13px' }}>
                            {candidateDetails?.candidate?.overallExperience}{' '}
                            Years
                          </Typography>
                        </Box>
                      )}
                      {candidateDetails?.candidate?.workStatus?.name && (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 0.5,
                          }}>
                          <RocketLaunchIcon
                            sx={{ color: '#ef4444', fontSize: '16px' }}
                          />
                          <Typography
                            sx={{ color: '#6b7280', fontSize: '13px' }}>
                            {candidateDetails?.candidate?.workStatus?.name}
                          </Typography>
                        </Box>
                      )}
                      {candidateDetails?.candidate?.identity && (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 0.5,
                          }}>
                          <DomainIcon
                            sx={{ color: '#6b7280', fontSize: '16px' }}
                          />
                          <Typography
                            sx={{
                              color: '#6b7280',
                              fontSize: '12px',
                              fontWeight: 400,
                            }}>
                            {candidateDetails?.candidate?.identity}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    sx={{
                      color: '#9ca3af',
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}>
                    Match Score
                  </Typography>
                  <Typography
                    sx={{
                      color: '#10b981',
                      fontSize: '38px',
                      fontWeight: 900,
                      lineHeight: 1,
                    }}>
                    {matchScore || 0}%
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#f3f4f6',
                      borderRadius: '2px',
                      height: '4px',
                      ml: 'auto',
                      mt: 1,
                      overflow: 'hidden',
                      width: '60px',
                    }}>
                    <Box
                      sx={{
                        bgcolor: '#3b82f6',
                        height: '100%',
                        width: `${matchScore || 0}%`,
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Score Breakdown - Grid Layout */}
              {Array.isArray(percentageData) && percentageData?.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    mb: 4,
                  }}>
                  {percentageData.map((row: any, index: number) => {
                    if (
                      row?.component === 'Identity Match' ||
                      row?.component === 'Secondary Skills'
                    ) {
                      return null;
                    }

                    const score = parseFloat(row?.score) || 0;
                    const max = parseFloat(row?.maxPercentage) || 0;
                    const percentage = Math.min(
                      max > 0 ? (score / max) * 100 : 0,
                      100,
                    );
                    const validPercentage = isNaN(percentage)
                      ? 0
                      : Math.max(0, percentage);
                    const colors = [
                      '#a855f7',
                      '#06b6d4',
                      '#f59e0b',
                      '#10b981',
                      '#6366f1',
                    ];
                    const color = colors[index % colors.length];

                    const formattedScore =
                      score % 1 === 0 ? score.toString() : score.toFixed(1);
                    const formattedMax = Math.round(max).toString();

                    return (
                      <Box key={index}>
                        <Typography
                          sx={{
                            color: '#6b7280',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '10px',
                            fontWeight: 500,
                            letterSpacing: '0.5px',
                            mb: 0.5,
                            textTransform: 'uppercase',
                          }}>
                          {row?.component || ''}
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: '#e5e7eb',
                            borderRadius: '2px',
                            height: '6px',
                            mb: 0.5,
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                          }}>
                          <Box
                            sx={{
                              bgcolor: color,
                              height: '100%',
                              transition: 'width 0.3s ease',
                              width: `${validPercentage}%`,
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            color: '#111827',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '11px',
                            fontWeight: 600,
                            textAlign: 'right',
                          }}>
                          {formattedScore}/{formattedMax}
                        </Typography>
                      </Box>
                    );
                  })}

                  {/* Identity Match */}
                  {/* {identityPercentage && (
                    <Box sx={{ gridColumn: 'span 3' }}>
                      <Typography
                        sx={{
                          color: '#6b7280',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '10px',
                          fontWeight: 500,
                          letterSpacing: '0.5px',
                          mb: 0.5,
                          textTransform: 'uppercase',
                        }}>
                        {identityPercentage?.component}
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: (() => {
                            const status =
                              identityPercentage?.identityStatus?.toLowerCase();
                            if (status?.includes('exact match'))
                              return '#10b981';
                            if (status?.includes('related role'))
                              return '#f59e0b';
                            if (status?.includes('role mismatch'))
                              return '#ef4444';
                            return '#10b981';
                          })(),
                          borderRadius: '2px',
                          height: '6px',
                          mb: 0.5,
                          width: '100%',
                        }}
                      />
                      <Typography
                        sx={{
                          color: (() => {
                            const status =
                              identityPercentage?.identityStatus?.toLowerCase();
                            if (status?.includes('exact match'))
                              return '#047857';
                            if (status?.includes('related role'))
                              return '#b45309';
                            if (status?.includes('role mismatch'))
                              return '#dc2626';
                            return '#047857';
                          })(),
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '11px',
                          fontWeight: 600,
                          textAlign: 'right',
                        }}>
                        ✓ {identityPercentage?.identityStatus} (
                        {identityPercentage?.identityPercentage}%)
                      </Typography>
                    </Box>
                  )} */}
                </Box>
              )}

              {/* AI Summary Text */}
              {candidateDetails?.justification && (
                <Box
                  sx={{
                    bgcolor: '#f9fafb',
                    borderRadius: '8px',
                    mb: 3,
                    p: 2,
                  }}>
                  <Typography
                    sx={{
                      color: '#374151',
                      fontSize: '14px',
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                    }}>
                    {candidateDetails?.justification}
                  </Typography>
                </Box>
              )}

              {/* Role/Title Match Analysis */}
              {(() => {
                const roleTitleMatch = (percentageData ?? []).find(
                  (row: any) => row?.component === 'Role/Title Match',
                );
                if (!roleTitleMatch) return null;

                const roleHistory = roleTitleMatch?.roleHistory || [];
                const analysis = roleTitleMatch?.roleTitleAnalysis || {};
                const formulas = analysis?.formulas || {};

                return (
                  <Box
                    sx={{
                      borderTop: '1px solid #e5e7eb',
                      mb: 4,
                      pt: 4,
                    }}>
                    <Typography
                      sx={{
                        color: '#374151',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        mb: 1.5,
                      }}>
                      Role/Title score calculation
                    </Typography>

                    <Box
                      sx={{
                        background:
                          'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
                        border: '1px solid #f9a8d4',
                        borderRadius: '8px',
                        mb: 2,
                        p: '12px 16px',
                      }}>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          sx={{
                            color: '#374151',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                          }}>
                          Score:{' '}
                          <Typography
                            component="span"
                            sx={{
                              color: (() => {
                                const score =
                                  analysis.calculatedScore ||
                                  roleTitleMatch?.score ||
                                  0;
                                const maxScore =
                                  analysis.maxScore ||
                                  roleTitleMatch?.maxPercentage ||
                                  35;
                                const percentage = (score / maxScore) * 100;

                                if (percentage >= 85) return '#16a34a'; // Green
                                if (percentage >= 70) return '#eab308'; // Yellow
                                if (percentage >= 50) return '#f97316'; // Orange
                                return '#dc2626'; // Red
                              })(),
                              fontSize: '0.95rem',
                              fontWeight: 700,
                            }}>
                            {analysis.calculatedScore || roleTitleMatch?.score}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              color: '#9ca3af',
                              fontSize: '0.9rem',
                              fontWeight: 400,
                            }}>
                            {' '}
                            /{' '}
                            {analysis.maxScore ||
                              roleTitleMatch?.maxPercentage}{' '}
                            points
                          </Typography>
                        </Typography>
                      </Box>

                      {analysis.calculationSteps &&
                        analysis.calculationSteps.length > 0 && (
                          <Box
                            sx={{
                              bgcolor: 'rgba(236, 72, 153, 0.08)',
                              borderLeft: '3px solid #ec4899',
                              borderRadius: '4px',
                              mb: 1,
                              overflow: 'hidden',
                              p: 1.5,
                            }}>
                            <Typography
                              sx={{
                                color: '#4b5563',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                mb: 1.25,
                              }}>
                              Calculation:
                            </Typography>
                            {analysis.calculationSteps.map(
                              (step: any, index: number) => (
                                <Box
                                  key={index}
                                  sx={{
                                    alignItems: 'center',
                                    borderBottom:
                                      index <
                                      analysis.calculationSteps.length - 1
                                        ? '1px solid rgba(255, 255, 255, 0.6)'
                                        : 'none',
                                    display: 'flex',
                                    pb:
                                      index <
                                      analysis.calculationSteps.length - 1
                                        ? 1.25
                                        : 0,
                                    pt: index > 0 ? 1.25 : 0,
                                  }}>
                                  <Box
                                    sx={{
                                      bgcolor: 'rgba(236, 72, 153, 0.3)',
                                      borderRadius: '3px',
                                      color: '#831843',
                                      flexShrink: 0,
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      mr: 1.5,
                                      px: 1.25,
                                      py: 0.5,
                                    }}>
                                    Step {step.step}
                                  </Box>
                                  <Typography
                                    sx={{
                                      color: '#374151',
                                      flex: 1,
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      lineHeight: 1.8,
                                    }}>
                                    {step.value}
                                  </Typography>
                                </Box>
                              ),
                            )}
                          </Box>
                        )}

                      <Box
                        sx={{
                          borderTop: '1px dashed #e5e7eb',
                          mt: 1,
                          pt: 1,
                        }}>
                        <Typography
                          sx={{
                            color: '#6b7280',
                            fontSize: '0.8rem',
                            lineHeight: 1.5,
                          }}>
                          Aggregation: {analysis.aggregationMethod}; Trajectory:{' '}
                          {analysis.trajectory}; T_adj ={' '}
                          {analysis.trajectory === 'ascending'
                            ? '+0.05'
                            : analysis.trajectory === 'descending'
                            ? '-0.05'
                            : '0'}
                          ; {roleHistory.length}(roles) evaluated; S_agg:{' '}
                          {analysis.s_agg}; W_current: {analysis.w_current};
                          W_best: {analysis.w_best_historical}
                        </Typography>
                      </Box>

                      {Object.keys(formulas).length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Box
                            onClick={() =>
                              setFormulaStepsExpanded(!formulaStepsExpanded)
                            }
                            sx={{
                              '&:hover': {
                                opacity: 0.8,
                              },
                              alignItems: 'center',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}>
                            <Typography
                              sx={{
                                alignItems: 'center',
                                color: '#4b5563',
                                display: 'flex',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                gap: 0.5,
                              }}>
                              <Typography
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                  fontSize: '14px',
                                  transform: formulaStepsExpanded
                                    ? 'rotate(90deg)'
                                    : 'rotate(0deg)',
                                  transition: 'transform 0.2s',
                                }}>
                                ›
                              </Typography>
                              Formula steps
                            </Typography>
                          </Box>

                          {formulaStepsExpanded && (
                            <Box
                              sx={{
                                background: 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '6px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                mt: 1,
                                p: '10px',
                              }}>
                              {Object.entries(formulas).map(
                                ([key, value]: [string, any]) => (
                                  <Box key={key}>
                                    <Typography
                                      sx={{
                                        color: '#4b5563',
                                        fontSize: '0.8rem',
                                      }}>
                                      {/* <Typography
                                        component="span"
                                        sx={{
                                          color: '#831843',
                                          fontFamily: 'monospace',
                                          fontWeight: 500,
                                          mr: 0.75,
                                          textTransform: 'capitalize',
                                        }}>
                                        {key
                                          .replace(/_/g, ' ')
                                          .replace(/^step(\d+) /, 'Step$1 ')}
                                        :
                                      </Typography> */}
                                      {String(value)}
                                    </Typography>
                                  </Box>
                                ),
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    {roleHistory.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography
                          sx={{
                            color: '#374151',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            mb: 1,
                          }}>
                          Role History (weighted)
                        </Typography>
                        <Box
                          sx={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            overflow: 'hidden',
                          }}>
                          <Box
                            component="table"
                            sx={{
                              borderCollapse: 'collapse',
                              fontSize: '0.8rem',
                              width: '100%',
                            }}>
                            <Box component="thead">
                              <Box component="tr">
                                <Box
                                  component="th"
                                  sx={{
                                    background: 'rgba(236, 72, 153, 0.12)',
                                    border: '1px solid #e5e7eb',
                                    color: '#831843',
                                    fontWeight: 600,
                                    p: '6px 10px',
                                    textAlign: 'left',
                                  }}>
                                  Title
                                </Box>
                                <Box
                                  component="th"
                                  sx={{
                                    background: 'rgba(236, 72, 153, 0.12)',
                                    border: '1px solid #e5e7eb',
                                    color: '#831843',
                                    fontWeight: 600,
                                    p: '6px 10px',
                                    textAlign: 'left',
                                    width: '60px',
                                  }}>
                                  Rank
                                </Box>
                                <Box
                                  component="th"
                                  sx={{
                                    background: 'rgba(236, 72, 153, 0.12)',
                                    border: '1px solid #e5e7eb',
                                    color: '#831843',
                                    fontWeight: 600,
                                    p: '6px 10px',
                                    textAlign: 'left',
                                    width: '70px',
                                  }}>
                                  S_base
                                </Box>
                                <Box
                                  component="th"
                                  sx={{
                                    background: 'rgba(236, 72, 153, 0.12)',
                                    border: '1px solid #e5e7eb',
                                    color: '#831843',
                                    fontWeight: 600,
                                    p: '6px 10px',
                                    textAlign: 'left',
                                    width: '80px',
                                  }}>
                                  W_role
                                </Box>
                                <Box
                                  component="th"
                                  sx={{
                                    background: 'rgba(236, 72, 153, 0.12)',
                                    border: '1px solid #e5e7eb',
                                    color: '#831843',
                                    fontWeight: 600,
                                    p: '6px 10px',
                                    textAlign: 'left',
                                  }}>
                                  W_role_calculation
                                </Box>
                                <Box
                                  component="th"
                                  sx={{
                                    background: 'rgba(236, 72, 153, 0.12)',
                                    border: '1px solid #e5e7eb',
                                    color: '#831843',
                                    fontWeight: 600,
                                    p: '6px 10px',
                                    textAlign: 'left',
                                  }}>
                                  Tenure_calculation
                                </Box>
                                <Box
                                  component="th"
                                  sx={{
                                    background: 'rgba(236, 72, 153, 0.12)',
                                    border: '1px solid #e5e7eb',
                                    color: '#831843',
                                    fontWeight: 600,
                                    p: '6px 10px',
                                    textAlign: 'left',
                                  }}>
                                  Reason
                                </Box>
                              </Box>
                            </Box>
                            <Box component="tbody">
                              {roleHistory.map((role: any, index: number) => (
                                <Box
                                  key={index}
                                  component="tr"
                                  sx={{
                                    '&:nth-of-type(even)': {
                                      background: 'rgba(255, 255, 255, 0.6)',
                                    },
                                  }}>
                                  <Box
                                    component="td"
                                    sx={{
                                      border: '1px solid #e5e7eb',
                                      color: '#374151',
                                      p: '6px 10px',
                                    }}>
                                    {role.designation}
                                  </Box>
                                  <Box
                                    component="td"
                                    sx={{
                                      border: '1px solid #e5e7eb',
                                      color: '#6b7280',
                                      p: '6px 10px',
                                    }}>
                                    {role.rank}
                                  </Box>
                                  <Box
                                    component="td"
                                    sx={{
                                      border: '1px solid #e5e7eb',
                                      color: '#6b7280',
                                      p: '6px 10px',
                                    }}>
                                    {role.s_base}
                                  </Box>
                                  <Box
                                    component="td"
                                    sx={{
                                      border: '1px solid #e5e7eb',
                                      color: '#6b7280',
                                      p: '6px 10px',
                                    }}>
                                    {role.w_role?.toFixed(2)}
                                  </Box>
                                  <Box
                                    component="td"
                                    sx={{
                                      border: '1px solid #e5e7eb',
                                      p: '6px 10px',
                                    }}>
                                    <Box
                                      sx={{
                                        background: 'rgba(251, 207, 232, 0.3)',
                                        borderRadius: '4px',
                                        color: '#831843',
                                        display: 'inline-block',
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        px: 1,
                                        py: 0.5,
                                      }}>
                                      {role.w_role_calculation}
                                    </Box>
                                  </Box>
                                  <Box
                                    component="td"
                                    sx={{
                                      border: '1px solid #e5e7eb',
                                      p: '6px 10px',
                                    }}>
                                    <Box
                                      sx={{
                                        background: 'rgba(251, 207, 232, 0.3)',
                                        borderRadius: '4px',
                                        color: '#831843',
                                        display: 'inline-block',
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        px: 1,
                                        py: 0.5,
                                      }}>
                                      {role.tenure_calculation || 'N/A'}
                                    </Box>
                                  </Box>
                                  <Box
                                    component="td"
                                    sx={{
                                      border: '1px solid #e5e7eb',
                                      color: '#6b7280',
                                      p: '6px 10px',
                                    }}>
                                    {role.reason}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Strengths Section */}
              {/* {Array.isArray(candidateDetails?.strengths) &&
                candidateDetails?.strengths?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        alignItems: 'center',
                        color: '#4b5563',
                        display: 'flex',
                        fontSize: '13px',
                        fontWeight: 700,
                        gap: 0.5,
                        mb: 1.5,
                      }}>
                      ✓ Strengths
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {candidateDetails?.strengths?.map((s, i) => (
                        <Box
                          key={i}
                          sx={{
                            bgcolor: '#dcfce7',
                            border: '1px solid #bbf7d0',
                            borderRadius: '4px',
                            color: '#166534',
                            fontSize: '11px',
                            px: 1.5,
                            py: 0.5,
                          }}>
                          ✓ {s}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )} */}
              {/* Considerations Section */}
              {/* {Array.isArray(candidateDetails?.gapsConcerns) &&
                candidateDetails?.gapsConcerns?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        alignItems: 'center',
                        color: '#4b5563',
                        display: 'flex',
                        fontSize: '13px',
                        fontWeight: 700,
                        gap: 0.5,
                        mb: 1.5,
                      }}>
                      ⚠ Considerations
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {candidateDetails?.gapsConcerns?.map((c, i) => (
                        <Box
                          key={i}
                          sx={{
                            bgcolor: '#fee2e2',
                            border: '1px solid #fecaca',
                            borderRadius: '4px',
                            color: '#991b1b',
                            fontSize: '11px',
                            px: 1.5,
                            py: 0.5,
                          }}>
                          ⚠ {c}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )} */}
              {(primarySkillAnalysis || secondarySkillAnalysis) && (
                <Box sx={{ borderTop: '1px solid #e5e7eb', mb: 3, pt: 4 }}>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 3,
                    }}>
                    <Typography
                      sx={{
                        color: '#111827',
                        fontSize: '13px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}>
                      SKILL ANALYSIS
                    </Typography>
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                      <CheckCircleIcon
                        sx={{ color: '#10b981', fontSize: '16px' }}
                      />
                      <Typography
                        sx={{
                          color: '#374151',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}>
                        {matchedSkills?.length} Primary Skill
                      </Typography>
                    </Box>
                  </Box>

                  {/* Primary Matched Skills Static Card */}
                  {primarySkillAnalysis && (
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          bgcolor: '#dbeafe',
                          borderRadius: '8px',
                          display: 'flex',
                          gap: 2,
                          px: 2,
                          py: 1.5,
                        }}>
                        <Box
                          sx={{
                            bgcolor: '#10b981',
                            borderRadius: '2px',
                            height: '24px',
                            width: '3px',
                          }}
                        />
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 4,
                          }}>
                          <Typography
                            sx={{
                              color: '#374151',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}>
                            Primary Skills:
                          </Typography>
                          <Typography
                            sx={{
                              color: '#374151',
                              fontFamily: 'JetBrains Mono, monospace',
                              fontSize: '12px',
                              letterSpacing: '-0.3px',
                            }}>
                            {primarySkillAnalysis?.calculation}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Secondary Matched Skills Static Card */}
                  {secondarySkills.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          bgcolor: '#faf5ff',
                          borderRadius: '8px',
                          display: 'flex',
                          gap: 2,
                          px: 2,
                          py: 1.5,
                        }}>
                        <Box
                          sx={{
                            bgcolor: '#8b5cf6',
                            borderRadius: '2px',
                            height: '24px',
                            width: '3px',
                          }}
                        />
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 4,
                          }}>
                          <Typography
                            sx={{
                              color: '#6b21a8',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}>
                            Secondary Skills:
                          </Typography>
                          <Typography
                            sx={{
                              color: '#374151',
                              fontFamily: 'JetBrains Mono, monospace',
                              fontSize: '12px',
                              letterSpacing: '-0.3px',
                            }}>
                            {secondarySkillAnalysis?.calculation}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Primary Matched Skills */}
              {Array.isArray(matchedSkills) && matchedSkills?.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      alignItems: 'center',
                      color: '#10b981',
                      display: 'flex',
                      fontSize: '11px',
                      fontWeight: 600,
                      gap: 0.5,
                      mb: 1.5,
                      textTransform: 'uppercase',
                    }}>
                    <CheckCircleIcon
                      sx={{ color: '#10b981', fontSize: '14px' }}
                    />{' '}
                    PRIMARY SKILLS MATCHED ({matchedSkills?.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {matchedSkills?.map((s, i) => (
                      <Box
                        key={i}
                        sx={{
                          bgcolor:
                            s?.score === 0.7
                              ? 'rgba(99,102,241,0.1)'
                              : '#dcfce7',
                          borderRadius: '6px',
                          color: s?.score === 0.7 ? '#6366f1' : '#15803d',
                          fontSize: '11px',
                          fontWeight: 500,
                          px: 1.5,
                          py: 0.5,
                        }}>
                        {s?.matchType === 'direct_primary'
                          ? '✓✓'
                          : s?.matchType === 'direct_secondary'
                          ? '✓'
                          : s?.matchType === 'ecosystem_primary'
                          ? '≈'
                          : s?.matchType === 'ecosystem_secondary'
                          ? '~'
                          : ''}{' '}
                        {['ecosystem_primary', 'ecosystem_secondary'].includes(
                          s?.matchType,
                        ) ? (
                          <span>
                            <span
                              style={{
                                fontWeight: 'bold',
                              }}>
                              {`${String(s?.skill)}`}
                            </span>{' '}
                            {'→'} <span>{`${String(s?.matchedWith)}`}</span>{' '}
                            {s?.score ? `(${s.score})` : ''}
                          </span>
                        ) : (
                          <span>
                            {s?.matchedWith} {s?.score ? `(${s.score})` : ''}
                          </span>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}

              {/* Secondary Skills */}
              {Array.isArray(secondarySkills) && secondarySkills?.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      alignItems: 'center',
                      color: '#15803d',
                      display: 'flex',
                      fontSize: '13px',
                      fontWeight: 700,
                      gap: 0.5,
                      mb: 1.5,
                    }}>
                    ✓ Secondary Matched Skills ({secondarySkills?.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {secondarySkills?.map((s, i) => (
                      <Box
                        sx={{
                          bgcolor:
                            s?.score === 0.7
                              ? 'rgba(99,102,241,0.1)'
                              : '#f0fdf4',
                          border:
                            s?.score === 0.7
                              ? '1px solid #c7d2fe'
                              : '1px solid #dcfce7',
                          borderRadius: '4px',
                          color: s?.score === 0.7 ? '#6366f1' : '#15803d',
                          fontSize: '11px',
                          px: 1.5,
                          py: 0.5,
                        }}>
                        {s?.matchType === 'direct_primary'
                          ? '✓✓'
                          : s?.matchType === 'direct_secondary'
                          ? '✓'
                          : s?.matchType === 'ecosystem_primary'
                          ? '≈'
                          : s?.matchType === 'ecosystem_secondary'
                          ? '~'
                          : ''}{' '}
                        {['ecosystem_primary', 'ecosystem_secondary'].includes(
                          s?.matchType,
                        ) ? (
                          <span>
                            <span
                              style={{
                                fontWeight: 'bold',
                              }}>
                              {`${String(s?.skill)}`}
                            </span>{' '}
                            {'→'} <span>{`${String(s?.matchedWith)}`}</span>{' '}
                            {s?.score ? `(${s.score})` : ''}
                          </span>
                        ) : (
                          <span>
                            {s?.matchedWith} {s?.score ? `(${s.score})` : ''}
                          </span>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}

              {/* Top Skills */}
              {/* {Array.isArray(candidateDetails?.topSkills) &&
                candidateDetails?.topSkills?.length > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        color: '#9ca3af',
                        fontSize: '12px',
                        fontWeight: 700,
                        mb: 1.5,
                        textTransform: 'uppercase',
                      }}>
                      Top Skills
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {candidateDetails?.topSkills?.map((skill, i) => (
                        <Box
                          key={i}
                          sx={{
                            bgcolor: '#dbeafe',
                            borderRadius: '99px',
                            color: '#1e40af',
                            fontSize: '12px',
                            fontWeight: 500,
                            px: 1.5,
                            py: 0.5,
                          }}>
                          {skill}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )} */}
            </Box>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};
